import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { geoAlbersUsa, geoEqualEarth, geoMercator, geoPath } from 'd3-geo';
import { feature } from 'topojson-client';
import worldTopology from 'world-atlas/countries-110m.json';
import usTopology from 'us-atlas/states-10m.json';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Collapse,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import TableRowsRoundedIcon from '@mui/icons-material/TableRowsRounded';
import {
  aggregateCityRows,
  aggregateLocationRows,
  aggregateRegionRows,
  formatLocationMetric,
  getAvailableLocationMetrics,
  normalizeLocationRow,
} from './locationData';

const MAP_WIDTH = 720;
const MAP_HEIGHT = 320;
const WORLD_FEATURES = feature(worldTopology, worldTopology.objects.countries).features
  .filter((item) => item.properties?.name !== 'Antarctica');
const US_STATE_FEATURES = feature(usTopology, usTopology.objects.states).features;
const WORLD_COLLECTION = { type: 'FeatureCollection', features: WORLD_FEATURES };
const US_COLLECTION = { type: 'FeatureCollection', features: US_STATE_FEATURES };
const WORLD_PATH = geoPath(geoEqualEarth().fitExtent([[8, 8], [MAP_WIDTH - 8, MAP_HEIGHT - 8]], WORLD_COLLECTION));
const US_PATH = geoPath(geoAlbersUsa().fitExtent([[18, 18], [MAP_WIDTH - 18, MAP_HEIGHT - 18]], US_COLLECTION));

const NAME_ALIASES = {
  'united states of america': 'united states',
  'dem rep congo': 'democratic republic of the congo',
  'dominican rep': 'dominican republic',
  'central african rep': 'central african republic',
  'eq guinea': 'equatorial guinea',
  'bosnia and herz': 'bosnia and herzegovina',
  'solomon is': 'solomon islands',
  's sudan': 'south sudan',
};

const STATE_ABBREVIATIONS = {
  al: 'alabama', ak: 'alaska', az: 'arizona', ar: 'arkansas', ca: 'california', co: 'colorado',
  ct: 'connecticut', de: 'delaware', dc: 'district of columbia', fl: 'florida', ga: 'georgia',
  hi: 'hawaii', id: 'idaho', il: 'illinois', in: 'indiana', ia: 'iowa', ks: 'kansas', ky: 'kentucky',
  la: 'louisiana', me: 'maine', md: 'maryland', ma: 'massachusetts', mi: 'michigan', mn: 'minnesota',
  ms: 'mississippi', mo: 'missouri', mt: 'montana', ne: 'nebraska', nv: 'nevada', nh: 'new hampshire',
  nj: 'new jersey', nm: 'new mexico', ny: 'new york', nc: 'north carolina', nd: 'north dakota',
  oh: 'ohio', ok: 'oklahoma', or: 'oregon', pa: 'pennsylvania', ri: 'rhode island', sc: 'south carolina',
  sd: 'south dakota', tn: 'tennessee', tx: 'texas', ut: 'utah', vt: 'vermont', va: 'virginia',
  wa: 'washington', wv: 'west virginia', wi: 'wisconsin', wy: 'wyoming',
};

const normalizeMapName = (value) => {
  const normalized = String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, ' ')
    .trim()
    .toLowerCase();
  return NAME_ALIASES[normalized] || normalized;
};

const normalizeStateName = (value) => {
  const normalized = normalizeMapName(value);
  return STATE_ABBREVIATIONS[normalized] || normalized;
};

const activateWithKeyboard = (event, action) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    action();
  }
};

const colorForValue = (theme, value, maxValue, empty = false) => {
  if (empty || Number(value) <= 0) return alpha(theme.palette.text.primary, 0.07);
  const intensity = Math.sqrt(Number(value) / Math.max(Number(maxValue), 1));
  return alpha(theme.palette.primary.main, 0.2 + (intensity * 0.72));
};

const bubbleSize = (value, maxValue, min = 64, max = 112) => {
  const intensity = Math.sqrt(Number(value || 0) / Math.max(Number(maxValue || 0), 1));
  return min + ((max - min) * intensity);
};

const BubbleCloud = ({ rows, metric, labelKey, title, onSelect }) => {
  const theme = useTheme();
  const visibleRows = rows.filter((row) => row[labelKey]).slice(0, 12);
  const maxValue = Math.max(...visibleRows.map((row) => Number(row[metric.key] || 0)), 1);

  if (!visibleRows.length) return <Alert severity="info">No local detail is available for this selection.</Alert>;

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 800 }}>{title}</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        {visibleRows.map((row) => {
          const size = bubbleSize(row[metric.key], maxValue);
          const label = row[labelKey];
          return (
            <ButtonBase
              key={label}
              disabled={!onSelect}
              onClick={() => onSelect?.(label)}
              aria-label={`${label}: ${formatLocationMetric(row[metric.key], metric.format)} ${metric.label}`}
              sx={{
                width: size,
                height: size,
                minWidth: size,
                borderRadius: '50%',
                p: 0.75,
                display: 'flex',
                flexDirection: 'column',
                bgcolor: colorForValue(theme, row[metric.key], maxValue),
                color: theme.palette.text.primary,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.38)}`,
                boxShadow: `0 5px 16px ${alpha(theme.palette.primary.main, 0.14)}`,
                '&.Mui-disabled': { color: theme.palette.text.primary, opacity: 1 },
                '&:focus-visible': { outline: `3px solid ${theme.palette.secondary.main}`, outlineOffset: 2 },
              }}
            >
              <Typography component="span" variant="caption" sx={{ fontWeight: 800, lineHeight: 1.05, textAlign: 'center' }}>{label}</Typography>
              <Typography component="span" variant="caption" sx={{ lineHeight: 1.2 }}>{formatLocationMetric(row[metric.key], metric.format)}</Typography>
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
};

BubbleCloud.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  metric: PropTypes.object.isRequired,
  labelKey: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  onSelect: PropTypes.func,
};

const SvgMapFrame = ({ label, children }) => (
  <Box
    sx={{
      width: '100%',
      maxWidth: 760,
      mx: 'auto',
      minWidth: 0,
      borderRadius: 2,
      overflow: 'hidden',
      '& svg': { display: 'block', width: '100%', height: 'auto', maxHeight: { xs: 260, md: 320 } },
      '& [role="button"]:focus-visible': { outline: '3px solid currentColor', outlineOffset: -3 },
    }}
  >
    <svg viewBox={`0 0 ${MAP_WIDTH} ${MAP_HEIGHT}`} role="img" aria-label={label} preserveAspectRatio="xMidYMid meet">
      {children}
    </svg>
  </Box>
);

SvgMapFrame.propTypes = { label: PropTypes.string.isRequired, children: PropTypes.node.isRequired };

const LocationMap = ({ rows = [], loading = false, error = '', countryScope = '', metricOptions, region, onRegionChange, onCitySelect, showDetailTable = true }) => {
  const theme = useTheme();
  const [metricKey, setMetricKey] = useState('users');
  const [selectedCountryCode, setSelectedCountryCode] = useState(countryScope);
  const [internalStateName, setInternalStateName] = useState('');
  const selectedStateName = region ?? internalStateName;
  const setSelectedStateName = useCallback(name => { setInternalStateName(name); onRegionChange?.(name); }, [onRegionChange]);
  const [selectedLocality, setSelectedLocality] = useState(null);
  const [tableOpen, setTableOpen] = useState(false);
  const aggregated = useMemo(() => aggregateLocationRows(countryScope ? rows.filter(row => normalizeLocationRow(row).countryCode === countryScope) : rows), [rows, countryScope]);
  const metrics = useMemo(() => metricOptions || getAvailableLocationMetrics(aggregated.countryRows), [aggregated.countryRows, metricOptions]);
  const selectedMetric = metrics.find((metric) => metric.key === metricKey) || metrics[0];

  const countryRowsByName = useMemo(() => new Map(
    aggregated.countryRows.map((row) => [normalizeMapName(row.country), row]),
  ), [aggregated.countryRows]);
  const featureRowPairs = useMemo(() => WORLD_FEATURES.map((countryFeature) => ({
    feature: countryFeature,
    row: countryRowsByName.get(normalizeMapName(countryFeature.properties?.name)),
  })), [countryRowsByName]);
  const selectedCountry = aggregated.countryRows.find((row) => row.countryCode === (countryScope || selectedCountryCode)) || null;
  const selectedCountryFeature = selectedCountry
    ? featureRowPairs.find((pair) => pair.row?.countryCode === selectedCountry.countryCode)?.feature
    : null;
  const countryMax = Math.max(...aggregated.countryRows.map((row) => Number(row[selectedMetric.key] || 0)), 1);
  const regionRows = useMemo(
    () => (selectedCountry ? aggregateRegionRows(aggregated.detailRows, selectedCountry.countryCode) : []),
    [aggregated.detailRows, selectedCountry],
  );
  const regionRowsByName = useMemo(() => new Map(regionRows.map((row) => [normalizeStateName(row.region), row])), [regionRows]);
  const selectedStateFeature = selectedStateName
    ? US_STATE_FEATURES.find((item) => normalizeStateName(item.properties?.name) === normalizeStateName(selectedStateName))
    : null;
  const cityRows = useMemo(
    () => (selectedCountry && selectedStateName
      ? aggregateCityRows(aggregated.detailRows, selectedCountry.countryCode, selectedStateName)
      : []),
    [aggregated.detailRows, selectedCountry, selectedStateName],
  );

  useEffect(() => {
    if (selectedMetric && selectedMetric.key !== metricKey) setMetricKey(selectedMetric.key);
  }, [metricKey, selectedMetric]);

  useEffect(() => {
    if (!countryScope && selectedCountryCode && !selectedCountry) {
      setSelectedCountryCode(countryScope);
      setSelectedStateName('');
    }
  }, [selectedCountry, selectedCountryCode, countryScope, setSelectedStateName]);

  const restart = () => {
    setSelectedCountryCode(countryScope);
    setSelectedStateName('');
    setSelectedLocality(null);
  };
  const goBack = () => {
    setSelectedLocality(null);
    if (selectedStateName) setSelectedStateName('');
    else if (!countryScope) setSelectedCountryCode('');
  };

  const renderCoordinateBubbles = (locationRows, projection) => {
    const positionedRows = locationRows
      .filter((row) => Number.isFinite(row.latitude) && Number.isFinite(row.longitude) && Number(row[selectedMetric.key] || 0) > 0)
      .sort((a, b) => Number(b[selectedMetric.key] || 0) - Number(a[selectedMetric.key] || 0))
      .slice(0, 100)
      .map((row) => ({ row, point: projection([row.longitude, row.latitude]) }))
      .filter(({ point }) => Array.isArray(point) && point.every(Number.isFinite));
    const maxValue = Math.max(...positionedRows.map(({ row }) => Number(row[selectedMetric.key] || 0)), 1);

    return positionedRows.map(({ row, point }) => {
      const radius = 4 + (Math.sqrt(Number(row[selectedMetric.key] || 0) / maxValue) * 13);
      const label = `${row.city}${row.region ? `, ${row.region}` : ''}`;
      return (
        <circle
          key={`city-${row.countryCode}-${row.region}-${row.city}`}
          cx={point[0]}
          cy={point[1]}
          r={radius}
          fill={alpha(theme.palette.secondary.main, 0.72)}
          stroke={theme.palette.background.paper}
          strokeWidth="1.75"
          role="button"
          tabIndex="0"
          aria-label={`Map point ${label}: ${formatLocationMetric(row[selectedMetric.key], selectedMetric.format)} ${selectedMetric.label}`}
          onClick={() => { setSelectedLocality(row); onCitySelect?.(row); }}
          onKeyDown={(event) => activateWithKeyboard(event, () => { setSelectedLocality(row); onCitySelect?.(row); })}
          style={{ cursor: 'pointer' }}
        >
          <title>{`${label}: ${formatLocationMetric(row[selectedMetric.key], selectedMetric.format)} ${selectedMetric.label}`}</title>
        </circle>
      );
    });
  };

  const detailRows = aggregated.detailRows.filter((row) => {
    if (selectedCountry && row.countryCode !== selectedCountry.countryCode) return false;
    if (selectedStateName && normalizeStateName(row.region) !== normalizeStateName(selectedStateName)) return false;
    return true;
  });
  const cityDetailCount = aggregated.detailRows.filter((row) => row.city).length;
  const coordinateDetailCount = aggregated.detailRows.filter((row) => row.city && Number.isFinite(row.latitude) && Number.isFinite(row.longitude)).length;
  const unmatchedRows = aggregated.countryRows.filter((row) => !featureRowPairs.some((pair) => pair.row?.countryCode === row.countryCode));
  const levelTitle = selectedStateName || selectedCountry?.country || 'Geographic traffic';
  const levelDescription = selectedStateName
    ? `City activity within ${selectedStateName}. Bubbles use GeoNames coordinates; size and heat represent ${selectedMetric.label.toLowerCase()}.`
    : selectedCountry?.countryCode === 'us'
      ? `Select a state to inspect local activity. State shading and bubbles represent ${selectedMetric.label.toLowerCase()}.`
      : selectedCountry
        ? `Focused ${selectedCountry.country} activity with coordinate-based city bubbles and regional detail.`
        : 'Select a country to focus the map. Countries with no reported activity are muted.';

  if (loading) return <Skeleton variant="rounded" height={320} aria-label="Loading geographic traffic map" />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!aggregated.detailRows.length && !aggregated.hasUnknown) {
    return <Alert severity="info">No mapped location data is available for the selected range.</Alert>;
  }

  const renderWorldMap = () => (
    <SvgMapFrame label={`World map of ${selectedMetric.label}`}>
      <rect width={MAP_WIDTH} height={MAP_HEIGHT} rx="12" fill={alpha(theme.palette.background.default, 0.46)} />
      {featureRowPairs.map(({ feature: countryFeature, row }) => {
        const interactive = Boolean(row);
        return (
          <path
            key={countryFeature.id || countryFeature.properties?.name}
            d={WORLD_PATH(countryFeature)}
            fill={colorForValue(theme, row?.[selectedMetric.key], countryMax, !row)}
            stroke={alpha(theme.palette.text.primary, interactive ? 0.36 : 0.14)}
            strokeWidth={interactive ? 0.75 : 0.4}
            role={interactive ? 'button' : undefined}
            tabIndex={interactive ? 0 : undefined}
            aria-label={interactive ? `${row.country}: ${formatLocationMetric(row[selectedMetric.key], selectedMetric.format)} ${selectedMetric.label}` : undefined}
            onClick={interactive ? () => { setSelectedCountryCode(row.countryCode); setSelectedLocality(null); } : undefined}
            onKeyDown={interactive ? (event) => activateWithKeyboard(event, () => { setSelectedCountryCode(row.countryCode); setSelectedLocality(null); }) : undefined}
            style={{ cursor: interactive ? 'pointer' : 'default', transition: 'fill 160ms ease, stroke-width 160ms ease' }}
          >
            <title>{interactive ? `${row.country}: ${formatLocationMetric(row[selectedMetric.key], selectedMetric.format)} ${selectedMetric.label}` : countryFeature.properties?.name}</title>
          </path>
        );
      })}
    </SvgMapFrame>
  );

  const renderCountryMap = () => {
    if (!selectedCountryFeature) return <Alert severity="info">A detailed boundary is unavailable for this country. Regional data remains below.</Alert>;
    const countryProjection = geoMercator().fitExtent([[36, 24], [MAP_WIDTH - 36, MAP_HEIGHT - 24]], selectedCountryFeature);
    const countryPath = geoPath(countryProjection);
    const countryLocations = aggregated.detailRows.filter((row) => row.countryCode === selectedCountry.countryCode);
    return (
      <SvgMapFrame label={`${selectedCountry.country} map showing ${selectedMetric.label}`}>
        <rect width={MAP_WIDTH} height={MAP_HEIGHT} rx="12" fill={alpha(theme.palette.background.default, 0.46)} />
        <path
          d={countryPath(selectedCountryFeature)}
          fill={colorForValue(theme, selectedCountry[selectedMetric.key], selectedCountry[selectedMetric.key])}
          stroke={theme.palette.primary.dark}
          strokeWidth="1.5"
        >
          <title>{`${selectedCountry.country}: ${formatLocationMetric(selectedCountry[selectedMetric.key], selectedMetric.format)} ${selectedMetric.label}`}</title>
        </path>
        {renderCoordinateBubbles(countryLocations, countryProjection)}
        <text x={MAP_WIDTH / 2} y={MAP_HEIGHT - 14} textAnchor="middle" fill={theme.palette.text.secondary} fontSize="13">
          {formatLocationMetric(selectedCountry[selectedMetric.key], selectedMetric.format)} {selectedMetric.label}
        </text>
      </SvgMapFrame>
    );
  };

  const renderUnitedStatesMap = () => {
    const regionMax = Math.max(...regionRows.map((row) => Number(row[selectedMetric.key] || 0)), 1);
    return (
      <SvgMapFrame label={`United States map of ${selectedMetric.label} by state`}>
        <rect width={MAP_WIDTH} height={MAP_HEIGHT} rx="12" fill={alpha(theme.palette.background.default, 0.46)} />
        {US_STATE_FEATURES.map((stateFeature) => {
          const stateName = stateFeature.properties?.name;
          const row = regionRowsByName.get(normalizeStateName(stateName));
          const interactive = Boolean(row);
          return (
            <path
              key={stateFeature.id}
              d={US_PATH(stateFeature)}
              fill={colorForValue(theme, row?.[selectedMetric.key], regionMax, !row)}
              stroke={alpha(theme.palette.text.primary, 0.42)}
              strokeWidth={interactive ? 0.9 : 0.5}
              role={interactive ? 'button' : undefined}
              tabIndex={interactive ? 0 : undefined}
              aria-label={interactive ? `${stateName}: ${formatLocationMetric(row[selectedMetric.key], selectedMetric.format)} ${selectedMetric.label}` : undefined}
              onClick={interactive ? () => { setSelectedStateName(row.region); setSelectedLocality(null); } : undefined}
              onKeyDown={interactive ? (event) => activateWithKeyboard(event, () => { setSelectedStateName(row.region); setSelectedLocality(null); }) : undefined}
              style={{ cursor: interactive ? 'pointer' : 'default' }}
            >
              <title>{interactive ? `${stateName}: ${formatLocationMetric(row[selectedMetric.key], selectedMetric.format)} ${selectedMetric.label}` : `${stateName}: no activity`}</title>
            </path>
          );
        })}
        {US_STATE_FEATURES.map((stateFeature) => {
          const row = regionRowsByName.get(normalizeStateName(stateFeature.properties?.name));
          if (!row || Number(row[selectedMetric.key] || 0) <= 0) return null;
          const [cx, cy] = US_PATH.centroid(stateFeature);
          if (!Number.isFinite(cx) || !Number.isFinite(cy)) return null;
          const radius = 3 + (Math.sqrt(Number(row[selectedMetric.key]) / regionMax) * 12);
          return (
            <circle
              key={`bubble-${stateFeature.id}`}
              cx={cx}
              cy={cy}
              r={radius}
              fill={alpha(theme.palette.secondary.main, 0.76)}
              stroke={theme.palette.background.paper}
              strokeWidth="1.5"
              role="button"
              tabIndex="0"
              aria-label={`Open ${row.region}: ${formatLocationMetric(row[selectedMetric.key], selectedMetric.format)} ${selectedMetric.label}`}
              onClick={() => { setSelectedStateName(row.region); setSelectedLocality(null); }}
              onKeyDown={(event) => activateWithKeyboard(event, () => { setSelectedStateName(row.region); setSelectedLocality(null); })}
              style={{ cursor: 'pointer' }}
            >
              <title>{`${row.region}: ${formatLocationMetric(row[selectedMetric.key], selectedMetric.format)} ${selectedMetric.label}`}</title>
            </circle>
          );
        })}
      </SvgMapFrame>
    );
  };

  const renderStateMap = () => {
    if (!selectedStateFeature) return <Alert severity="info">A detailed state boundary is unavailable. City activity remains below.</Alert>;
    const stateProjection = geoAlbersUsa().fitExtent([[42, 24], [MAP_WIDTH - 42, MAP_HEIGHT - 24]], selectedStateFeature);
    const statePath = geoPath(stateProjection);
    const stateRow = regionRowsByName.get(normalizeStateName(selectedStateName));
    return (
      <SvgMapFrame label={`${selectedStateName} map showing ${selectedMetric.label}`}>
        <rect width={MAP_WIDTH} height={MAP_HEIGHT} rx="12" fill={alpha(theme.palette.background.default, 0.46)} />
        <path
          d={statePath(selectedStateFeature)}
          fill={colorForValue(theme, stateRow?.[selectedMetric.key], stateRow?.[selectedMetric.key])}
          stroke={theme.palette.primary.dark}
          strokeWidth="1.75"
        >
          <title>{`${selectedStateName}: ${formatLocationMetric(stateRow?.[selectedMetric.key], selectedMetric.format)} ${selectedMetric.label}`}</title>
        </path>
        {renderCoordinateBubbles(cityRows, stateProjection)}
      </SvgMapFrame>
    );
  };

  return (
    <Stack spacing={1.5}>
      <Box sx={{ display: 'flex', alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{levelTitle}</Typography>
          <Typography variant="body2" color="text.secondary">{levelDescription}</Typography>
        </Box>
        <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap' }}>
          {selectedCountry && (!countryScope || selectedStateName) ? <Button size="small" startIcon={<ArrowBackRoundedIcon />} onClick={goBack}>Back</Button> : null}
          {selectedCountry ? <Button size="small" startIcon={<RestartAltRoundedIcon />} onClick={restart}>Restart</Button> : null}
          <FormControl size="small" sx={{ minWidth: { xs: 160, sm: 200 }, flexGrow: { xs: 1, sm: 0 } }}>
            <InputLabel id="location-map-metric-label">Map metric</InputLabel>
            <Select
              labelId="location-map-metric-label"
              label="Map metric"
              value={selectedMetric?.key || 'users'}
              onChange={(event) => setMetricKey(event.target.value)}
            >
              {metrics.map((metric) => <MenuItem key={metric.key} value={metric.key}>{metric.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Stack>
      </Box>

      <Paper variant="outlined" sx={{ p: { xs: 0.5, sm: 1 }, borderRadius: 2, overflow: 'hidden' }}>
        {!selectedCountry ? renderWorldMap() : null}
        {selectedCountry && selectedCountry.countryCode !== 'us' ? renderCountryMap() : null}
        {selectedCountry?.countryCode === 'us' && !selectedStateName ? renderUnitedStatesMap() : null}
        {selectedCountry?.countryCode === 'us' && selectedStateName ? renderStateMap() : null}
      </Paper>

      {selectedCountry && selectedCountry.countryCode !== 'us' ? (
        <BubbleCloud rows={regionRows} metric={selectedMetric} labelKey="region" title="Regional activity" />
      ) : null}
      {selectedCountry?.countryCode === 'us' && selectedStateName ? (
        <BubbleCloud
          rows={cityRows}
          metric={selectedMetric}
          labelKey="city"
          title={`City activity in ${selectedStateName}`}
          onSelect={(city) => { const row = cityRows.find(item => item.city === city) || null; setSelectedLocality(row); if (row) onCitySelect?.(row); }}
        />
      ) : null}

      {selectedLocality ? (
        <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }} aria-live="polite">
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{selectedLocality.city}, {selectedLocality.region || selectedLocality.country}</Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedMetric.label}: {formatLocationMetric(selectedLocality[selectedMetric.key], selectedMetric.format)}{!metricOptions && <> · Users: {formatLocationMetric(selectedLocality.users)} · Sessions: {formatLocationMetric(selectedLocality.sessions)} · Conversions: {formatLocationMetric(selectedLocality.conversions)}</>}
          </Typography>
        </Paper>
      ) : null}

      {aggregated.detailRows.some((row) => Number.isFinite(row.latitude) && Number.isFinite(row.longitude)) ? (
        <Typography variant="caption" color="text.secondary">
          {coordinateDetailCount} of {cityDetailCount} city rows placed using <Link href="https://www.geonames.org/" target="_blank" rel="noreferrer">GeoNames</Link> coordinates, licensed under CC BY 4.0. Bubble area represents {selectedMetric.label.toLowerCase()}.
        </Typography>
      ) : null}

      {showDetailTable && <Box>
        <Button
          size="small"
          startIcon={<TableRowsRoundedIcon />}
          onClick={() => setTableOpen((open) => !open)}
          aria-expanded={tableOpen}
          aria-controls="location-detail-table"
        >
          {tableOpen ? 'Hide' : 'Show'} location table ({detailRows.length})
        </Button>
        <Collapse in={tableOpen} unmountOnExit>
          <TableContainer id="location-detail-table" component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 300, maxWidth: '100%', overflow: 'auto' }}>
            <Table stickyHeader size="small" aria-label="Location performance detail">
              <TableHead><TableRow><TableCell><strong>Country</strong></TableCell><TableCell><strong>Region</strong></TableCell><TableCell><strong>City</strong></TableCell><TableCell align="right"><strong>Users</strong></TableCell><TableCell align="right"><strong>Sessions</strong></TableCell><TableCell align="right"><strong>Engagement</strong></TableCell><TableCell align="right"><strong>Conversions</strong></TableCell><TableCell align="right"><strong>Revenue</strong></TableCell></TableRow></TableHead>
              <TableBody>{detailRows.map((row) => <TableRow key={`${row.countryCode}-${row.region}-${row.city}`} hover><TableCell>{row.country}</TableCell><TableCell>{row.region || '—'}</TableCell><TableCell>{row.city || '—'}</TableCell><TableCell align="right">{formatLocationMetric(row.users)}</TableCell><TableCell align="right">{formatLocationMetric(row.sessions)}</TableCell><TableCell align="right">{formatLocationMetric(row.engagementRate, 'percent')}</TableCell><TableCell align="right">{formatLocationMetric(row.conversions)}</TableCell><TableCell align="right">{formatLocationMetric(row.revenue, 'currency')}</TableCell></TableRow>)}</TableBody>
            </Table>
          </TableContainer>
        </Collapse>
      </Box>}

      {aggregated.hasUnknown ? <Alert severity="info">Unknown or unmapped: {formatLocationMetric(aggregated.unknown[selectedMetric.key], selectedMetric.format)} {selectedMetric.label.toLowerCase()}. These rows remain in the summary but cannot be placed on the map.</Alert> : null}
      {unmatchedRows.length ? <Alert severity="info">{unmatchedRows.length} reported countr{unmatchedRows.length === 1 ? 'y is' : 'ies are'} available in the table but could not be matched to a boundary.</Alert> : null}
    </Stack>
  );
};

LocationMap.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  error: PropTypes.string,
  countryScope: PropTypes.oneOf(['', 'us']),
  metricOptions: PropTypes.arrayOf(PropTypes.object),
  region: PropTypes.string,
  onRegionChange: PropTypes.func,
  onCitySelect: PropTypes.func,
  showDetailTable: PropTypes.bool,
};

export default LocationMap;
